import React, { useState } from 'react';
import { useEffect } from 'react';
import { Bookmark } from 'lucide-react';
import PostCard from './PostCard';
import { postsService } from '../services/postsService';

import { supabase } from '../lib/supabase';

const SavedView: React.FC = () => {
  const [, setSavedPosts] = useState<Record<string, unknown>[]>([]);
  const [posts, setPosts] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSavedPosts();
  }, []);

  const loadSavedPosts = async () => {
    try {
      setLoading(true);
      const data = await getSavedPosts();
      setSavedPosts(data);
      setPosts(data);
    } catch (err) {
      setError('Error al cargar las publicaciones guardadas');
      console.error('Error loading saved posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSavedPosts = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('wishlist_items')
      .select(`
        *,
        posts (
          *,
          profiles!posts_author_id_fkey (
            id,
            username,
            full_name,
            avatar_url,
            is_premium
          ),
          media_files (
            id,
            type,
            url,
            file_name,
            file_size,
            duration,
            width,
            height
          )
        )
      `)
      .eq('user_id', user.id)
      .order('added_at', { ascending: false });

    if (error) throw error;
    return data?.map(item => item.posts) || [];
  };

  const handleLike = async (postId: string) => {
    try {
      const isLiked = await postsService.checkIfLiked(postId);
      if (isLiked) {
        await postsService.unlikePost(postId);
      } else {
        await postsService.likePost(postId);
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleSave = (postId: string) => {
    // Remove post from saved list immediately (optimistic update)
    setPosts(prev => prev.filter(post => post.id !== postId));
    
    // Then sync with server
    setTimeout(() => {
      loadSavedPosts();
    }, 100);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-200 h-48 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bookmark className="text-purple-600" size={24} />
            Obras Guardadas
          </h1>
          <p className="text-gray-600 mt-1">Tu arte favorito guardado</p>
        </div>
      </div>


      {/* Content */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {posts.map(post => (
            <PostCard
              key={post.id}
              post={{
                ...post,
                author: post.profiles || post.author || {
                  id: post.author_id || '',
                  username: 'usuario_desconocido',
                  full_name: 'Usuario Desconocido',
                  avatar_url: null,
                  is_premium: false
                },
                media: post.media_files || post.media || [],
                comments: post.comments || [],
                likes: post.likes_count || post.likes || 0,
                views: post.views_count || post.views || 0,
                createdAt: post.created_at || post.createdAt
              }}
              onLike={() => handleLike(post.id)}
              onSave={() => handleSave(post.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedView;